import { memo, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import styled, { css } from '@mint-lib/styled';

import { Box, Chip, Icon, Tooltip } from '@myn/mui';

import {
  BUILDER_ROW_TYPES,
  EMPTY_VALUE,
} from '../../../../../../constants/campaignBuilder.js';
import type { IOStatuses } from '../../../../../../types/insertionOrder.js';
import { getMapOrderStatusToChip } from '../../../../../../utils/insertionOrder.jsx';
import { BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';

export type Props = {
  value: IOStatuses | null;
  row: BuilderTableRow;
  cellColor: string;
};

const OrderStatusField: FC<Props> = memo(({ value, row, cellColor }) => {
  const { t } = useTranslation('@myn/planning');
  const { type, scale } = row.original;

  const isTileRow = type === BUILDER_ROW_TYPES.TILE;
  const defaultChipProps = { tooltip: '', label: value }; // prevent error if FE gets unexpected status value

  if (!isTileRow) {
    return null;
  }

  if (!value) {
    return (
      <FormattedField
        scale={scale}
        value={EMPTY_VALUE}
        color={cellColor}
        variant="text"
      />
    );
  }

  const { tooltip, label, color } =
    getMapOrderStatusToChip(t)[value] || defaultChipProps;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
      <StyledChip
        variant="filled"
        size="small"
        color={color}
        icon={
          <Tooltip title={tooltip}>
            <Box className="icon-container">
              <Icon component="Information" />
            </Box>
          </Tooltip>
        }
        label={label}
        $scale={scale}
      />
    </Box>
  );
});
export default OrderStatusField;

const StyledChip = styled(Chip)<{ $scale: number }>`
  ${({ $scale }) => css`
    & {
      font-size: ${0.75 * $scale}rem;
      line-height: ${$scale}rem;
      height: ${1.25 * $scale}rem;
      border-radius: ${12 * $scale}px;
      padding: ${2 * $scale}px ${4 * $scale}px;
    }

    .icon-container {
      display: flex;
      flex: 0 0 ${$scale}rem;
      height: ${$scale}rem;
    }

    svg {
      width: 100%;
      height: 100%;
    }
  `}
`;
